import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { PropsWithChildren } from 'react';
import { cn } from '@/utils/shad';
import { BarChart2, Info } from 'lucide-react';
import { EventFor, WithClassName, WithOnClick } from '@/types/component';
import { BasicTooltip } from '@/comps/basic-tooltip';
import { APP_SHELL_TOOLBAR_ROW_CLASS } from '@/comps/tabs/TableHeaderToolsContainer';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
type ReportChartCardProps = PropsWithChildren & WithClassName & WithOnClick<EventFor<'section', 'onClick'>> & {
  title?: string;
  description?: string;
  headerActions?: React.ReactNode;
  /** Always visible in the header (e.g. time range). */
  headerTrailing?: React.ReactNode;
  /** Extra classes on the title + description row (default grows to push actions right). */
  headerTitleClassName?: string;
  /** Extra classes on the title chip wrapper (e.g. max width for long titles). */
  titleChipClassName?: string;
  /** Extra classes on the right header cluster (`data-tour="chart-actions"`). */
  headerToolbarClassName?: string;
  /** When set, wraps `headerTrailing` in a div with these classes (e.g. scrollable legend). */
  headerTrailingClassName?: string;
  /** Merged with default content area padding (e.g. `p-0` for edge-to-edge chart bodies). */
  contentClassName?: string;
};
export function ReportChartCard(t0) {
  const $ = _c(49);
  const {
    children,
    title,
    description,
    headerActions,
    headerTrailing,
    headerTitleClassName,
    titleChipClassName,
    headerToolbarClassName,
    headerTrailingClassName,
    contentClassName,
    className,
    onClick
  } = t0;
  const isEdgeToEdgeContent = contentClassName?.includes("p-0");
  let t1;
  if ($[0] !== headerTrailing || $[1] !== headerTrailingClassName) {
    t1 = headerTrailing ? headerTrailingClassName ? <div className={headerTrailingClassName}>{headerTrailing}</div> : headerTrailing : null;
    $[0] = headerTrailing;
    $[1] = headerTrailingClassName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const trailingNode = t1;
  const t2 = onClick ? 0 : undefined;
  const t3 = onClick ? "button" : undefined;
  let t4;
  if ($[3] !== className) {
    t4 = cn("flex min-h-0 grow flex-col overflow-hidden", className);
    $[3] = className;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn(APP_SHELL_TOOLBAR_ROW_CLASS, "justify-between gap-2 border-b px-8");
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = headerTitleClassName ?? "flex-1";
  let t7;
  if ($[6] !== t6) {
    t7 = cn("flex h-full min-w-0 items-center gap-2 overflow-hidden", t6);
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== titleChipClassName) {
    t8 = cn("flex h-full w-fit max-w-full min-w-0 items-center border-b border-b-white!", titleChipClassName);
    $[8] = titleChipClassName;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <BarChart2 size={14} className="shrink-0" />;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = title ?? "untitled";
  const t11 = title ?? "untitled";
  let t12;
  if ($[11] !== t11) {
    t12 = <p className="line-clamp-1 text-xs">{t11}</p>;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t10 || $[14] !== t12) {
    t13 = <span className="flex min-w-0 items-center justify-center gap-2 rounded-lg border px-2 py-1">{t9}<BasicTooltip text={t10}>{t12}</BasicTooltip></span>;
    $[13] = t10;
    $[14] = t12;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== t13 || $[17] !== t8) {
    t14 = <div className={t8}>{t13}</div>;
    $[16] = t13;
    $[17] = t8;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== description) {
    t15 = !!description?.trim() && <div className="flex h-full shrink-0 items-center"><Tooltip><TooltipTrigger asChild={true}><button type="button" className="cursor-default p-1 text-muted-foreground outline-hidden focus-visible:ring-2 focus-visible:ring-ring" aria-label="Chart description" onClick={_temp}><Info className="size-3.5" strokeWidth={2} /></button></TooltipTrigger><TooltipContent side="bottom" align="start" className="text-left leading-snug">{description}</TooltipContent></Tooltip></div>;
    $[19] = description;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t14 || $[22] !== t15 || $[23] !== t7) {
    t16 = <div className={t7}>{t14}{t15}</div>;
    $[21] = t14;
    $[22] = t15;
    $[23] = t7;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== headerToolbarClassName) {
    t17 = cn("flex shrink-0 items-center gap-0", headerToolbarClassName);
    $[25] = headerToolbarClassName;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== headerActions) {
    t18 = headerActions ? <div className="mr-1.5 flex shrink-0 items-center gap-1">{headerActions}</div> : null;
    $[27] = headerActions;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t17 || $[30] !== t18 || $[31] !== trailingNode) {
    t19 = <div className={t17} data-tour="chart-actions">{t18}{trailingNode}</div>;
    $[29] = t17;
    $[30] = t18;
    $[31] = trailingNode;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t16 || $[34] !== t19) {
    t20 = <header className="shrink-0 bg-background"><div className={t5}>{t16}{t19}</div></header>;
    $[33] = t16;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const t21 = isEdgeToEdgeContent ? "flex flex-col overflow-hidden py-0" : "overflow-auto";
  let t22;
  if ($[36] !== contentClassName || $[37] !== t21) {
    t22 = cn("min-h-0 grow py-4", t21, "px-8", contentClassName);
    $[36] = contentClassName;
    $[37] = t21;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== children || $[40] !== t22) {
    t23 = <div className={t22}>{children}</div>;
    $[39] = children;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== onClick || $[43] !== t2 || $[44] !== t20 || $[45] !== t23 || $[46] !== t3 || $[47] !== t4) {
    t24 = <section tabIndex={t2} role={t3} className={t4} onClick={onClick}>{t20}{t23}</section>;
    $[42] = onClick;
    $[43] = t2;
    $[44] = t20;
    $[45] = t23;
    $[46] = t3;
    $[47] = t4;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  return t24;
}
function _temp(e) {
  return e.stopPropagation();
}